import {
  BASE_CATEGORY,
  CATEGORY_LABEL,
  WHERE_CATEGORIES,
  WHERE_FIELDS,
  type WhereCategory,
  type WhereDataset,
  type WhereField,
  type WhereOption,
  type WhereValueSource,
} from '@parcel/filters';

/** Value lists the field list cannot hold: the workspace's cities, tags, Views and score tiers. */
export type WhereOptions = Partial<Record<WhereValueSource | 'tiers', readonly WhereOption[]>>;

export interface WhereMenuCategory {
  category: WhereCategory;
  label: string;
  fields: readonly WhereField[];
}

export interface WhereMenuModel {
  /** Empty input: recent fields, else the first three of the base category. */
  suggested: readonly WhereField[];
  /** Empty input: the menu's first level, base category first. */
  categories: readonly WhereMenuCategory[];
  /** Typing: fields whose label (or "Category Label") matches. */
  fieldMatches: readonly WhereField[];
  /** Typing: listed or workspace values that match, e.g. "multi" finds Project use Multifamily. */
  valueMatches: ReadonlyArray<{ field: WhereField; option: WhereOption }>;
  /** Trimmed input; null when empty. */
  searchText: string | null;
}

const SUGGESTED_FALLBACK = 3;
const VALUE_MATCH_CAP = 6;
const VALUE_MATCH_PER_FIELD = 2;

export function fieldValues(field: WhereField, options: WhereOptions = {}): readonly WhereOption[] {
  if (field.options) return field.options;
  return field.source ? (options[field.source] ?? []) : [];
}

/** "Project | City" in the menu; a base field is just its label. */
export function menuLabel(
  dataset: WhereDataset,
  field: WhereField,
): { category: string | null; label: string } {
  return field.category === BASE_CATEGORY[dataset]
    ? { category: null, label: field.label }
    : { category: CATEGORY_LABEL[field.category], label: field.label };
}

export function deriveWhereMenu(
  input: string,
  dataset: WhereDataset,
  opts: { options?: WhereOptions; recentKeys?: readonly string[] } = {},
): WhereMenuModel {
  const fields = WHERE_FIELDS[dataset];
  const trimmed = input.trim();
  const text = trimmed.toLowerCase();
  if (!text) {
    const order = [
      BASE_CATEGORY[dataset],
      ...WHERE_CATEGORIES.filter((c) => c !== BASE_CATEGORY[dataset]),
    ];
    const categories = order
      .map((category) => ({
        category,
        label: CATEGORY_LABEL[category],
        fields: fields.filter((f) => f.category === category),
      }))
      .filter((c) => c.fields.length > 0);
    const recent = (opts.recentKeys ?? [])
      .map((key) => fields.find((f) => f.key === key))
      .filter((f): f is WhereField => Boolean(f));
    const suggested = recent.length > 0 ? recent : fields.slice(0, SUGGESTED_FALLBACK);
    return { suggested, categories, fieldMatches: [], valueMatches: [], searchText: null };
  }
  const fieldMatches = fields.filter((f) => {
    const named = `${CATEGORY_LABEL[f.category]} ${f.label}`.toLowerCase();
    return f.label.toLowerCase().includes(text) || named.includes(text);
  });
  const valueMatches: Array<{ field: WhereField; option: WhereOption }> = [];
  for (const field of fields) {
    if (field.key === 'view') continue;
    let perField = 0;
    for (const option of fieldValues(field, opts.options)) {
      if (valueMatches.length >= VALUE_MATCH_CAP || perField >= VALUE_MATCH_PER_FIELD) break;
      if (option.label.toLowerCase().includes(text) || option.value.toLowerCase().includes(text)) {
        valueMatches.push({ field, option });
        perField += 1;
      }
    }
    if (valueMatches.length >= VALUE_MATCH_CAP) break;
  }
  return { suggested: [], categories: [], fieldMatches, valueMatches, searchText: trimmed };
}
